import CloseIcon from '@mui/icons-material/Close'
import { useTheme } from '@/contexts/RADashboardThemeContext/hooks'
import React from 'react'

interface CovarianceParametersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matrixName: string;
}

export function CovarianceParametersDialog({
  open,
  onOpenChange,
  matrixName
}: CovarianceParametersDialogProps) {
  const theme = useTheme()
  
  // Map matrix values to their display details
  const matrixDetails: Record<string, any> = {
    'gcv14-base': {
      displayName: 'GCV14 EQ+FI+Comm - Base',
      description: 'Clearwater Wilshire Global Risk Model covering Equities, Fixed Income, and Commodities',
      parameters: {
        factors: '2270',
        assetClasses: 'Equities, Fixed Income, Commodities',
        estimationMethod: 'Standard averaging',
        timeSeriesWeight: 'Equal weight',
        timeSeriesLength: '18 Months daily factors',
        frequency: 'Monthly',
        currency: 'Multi-currency'
      }
    },
    'gcv14-extended': {
      displayName: 'GCV14 EQ+FI+Comm - Extended',
      description: 'Extended Clearwater Wilshire Global Risk Model with additional factors',
      parameters: {
        factors: '3500',
        assetClasses: 'Equities, Fixed Income, Commodities, Derivatives',
        estimationMethod: 'Exponential weighting',
        timeSeriesWeight: 'Exponentially declining',
        timeSeriesLength: '24 Months daily factors',
        frequency: 'Daily',
        currency: 'Multi-currency'
      }
    },
    'gcv14-short': {
      displayName: 'GCV14 Short Duration',
      description: 'Short-duration focused Clearwater Wilshire model for fixed income',
      parameters: {
        factors: '1800',
        assetClasses: 'Fixed Income, Short Duration',
        estimationMethod: 'Standard averaging',
        timeSeriesWeight: 'Equal weight',
        timeSeriesLength: '12 Months daily factors',
        frequency: 'Weekly',
        currency: 'Multi-currency'
      }
    },
    'barra': {
      displayName: 'Barra Global Equity Model',
      description: 'MSCI Barra global equity risk model',
      parameters: {
        factors: '1500',
        assetClasses: 'Equities',
        estimationMethod: 'Cross-sectional regression',
        timeSeriesWeight: 'Exponentially declining',
        timeSeriesLength: '24 Months daily factors',
        frequency: 'Daily',
        currency: 'Multi-currency'
      }
    }
  };

  const details = matrixDetails[matrixName] || matrixDetails['gcv14-base'];

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000
      }}
      onClick={() => onOpenChange(false)}
    >
      <div
        style={{
          backgroundColor: theme.cardBackground,
          borderRadius: '8px',
          width: '700px',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          margin: '16px'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '24px',
          borderBottom: `1px solid ${theme.border}`
        }}>
          <div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              margin: 0,
              color: theme.textPrimary
            }}>
              Covariance Matrix Parameters
            </h2>
            <p style={{
              fontSize: '16px',
              color: theme.textSecondary,
              margin: '4px 0 0 0'
            }}>
              Detailed parameters for {details.displayName}
            </p>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              color: theme.textSecondary
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px'
        }}>
          {/* Model Info Box */}
          <div style={{
            backgroundColor: theme.inputBackground,
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              fontWeight: '600',
              color: theme.textPrimary,
              marginBottom: '4px'
            }}>
              {details.displayName}
            </div>
            <p style={{
              fontSize: '16px',
              color: theme.textSecondary,
              margin: 0
            }}>
              {details.description}
            </p>
          </div>

          {/* Parameters Table */}
          <div style={{
            border: `1px solid ${theme.border}`,
            borderRadius: '8px',
            overflow: 'hidden',
            marginBottom: '24px'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: theme.inputBackground, borderBottom: `1px solid ${theme.border}` }}>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: theme.textPrimary
                  }}>
                    Parameter
                  </th>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: theme.textPrimary
                  }}>
                    Value
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'Factors', value: details.parameters.factors },
                  { label: 'Asset Classes', value: details.parameters.assetClasses },
                  { label: 'Estimation Method', value: details.parameters.estimationMethod },
                  { label: 'Time series weight', value: details.parameters.timeSeriesWeight },
                  { label: 'Time series length', value: details.parameters.timeSeriesLength },
                  { label: 'Frequency', value: details.parameters.frequency },
                  { label: 'Currency', value: details.parameters.currency },
                ].map((row, index, arr) => (
                  <tr key={row.label} style={index < arr.length - 1 ? { borderBottom: `1px solid ${theme.border}` } : undefined}>
                    <td style={{
                      padding: '12px 16px',
                      fontSize: '16px',
                      fontWeight: '500',
                      color: theme.textPrimary,
                      backgroundColor: theme.cardBackground
                    }}>
                      {row.label}
                    </td>
                    <td style={{
                      padding: '12px 16px',
                      fontSize: '16px',
                      color: theme.textSecondary,
                      backgroundColor: theme.cardBackground
                    }}>
                      {row.value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* About This Matrix */}
          <div style={{
            backgroundColor: theme.inputBackground,
            borderRadius: '8px',
            padding: '16px'
          }}>
            <div style={{
              fontWeight: '600',
              color: theme.textPrimary,
              marginBottom: '12px'
            }}>
              About This Matrix
            </div>
            <div style={{
              fontSize: '16px',
              color: theme.textSecondary,
              lineHeight: '1.5'
            }}>
              <p style={{ margin: '0 0 8px 0' }}>
                The Clearwater Wilshire GCV14 model covers global risk factors across asset classes:
              </p>
              <ul style={{
                margin: 0,
                paddingLeft: '20px',
                listStyleType: 'disc'
              }}>
                <li style={{ marginBottom: '4px' }}>32 Currency Markets</li>
                <li style={{ marginBottom: '4px' }}>Equity, ABS/CMBS USD Factors and Commodity Factors</li>
                <li style={{ marginBottom: '4px' }}>18 months daily factor returns, equal-weighted</li>
                <li>Includes Proxies for unmeasured factors</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          borderTop: `1px solid ${theme.border}`,
          padding: '16px 24px',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={() => onOpenChange(false)}
            style={{
              padding: '12px 24px',
              backgroundColor: theme.runButtonBackground,
              color: theme.runButtonColor,
              border: 'none',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
